"use client";

import Image from "next/image";
import CldImage from "@/app/utils/lib/CldImage";
import styles from "./GalleryTab.module.css";
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import {
  useScroll,
  useTransform,
  useSpring,
  motion,
} from "framer-motion";
import { ShowcaseImage } from "@/app/interfaces/showcaseImage";
import { FinishedEvent } from "@/app/interfaces/event";

// Helpers

function splitIntoColumns<T>(arr: T[], cols: number): T[][] {
  return Array.from({ length: cols }, (_, i) =>
    arr.filter((_, idx) => idx % cols === i)
  );
}

// Must match `top` on .desktopSticky / .mobileSticky (the fixed navbar height).
const STICKY_TOP_PX = 80;
// How much page scroll it takes to reveal 1px of hidden content.
// Higher = slower, more deliberate parallax.
const DESKTOP_SCROLL_FACTOR = 1.4;
const MOBILE_SCROLL_FACTOR = 1.2;

// Desktop:

function DesktopParallax({ columns, titleNode, eventTitleById }: { columns: ShowcaseImage[][], titleNode: React.ReactNode, eventTitleById: Map<string, string> }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const colRefs = useRef<(HTMLDivElement | null)[]>([]);

  // How far each column must translate so its last image ends fully visible.
  const [travels, setTravels] = useState<number[]>([0, 0, 0]);

  const { scrollYProgress } = useScroll({
    target: trackRef,
    // Progress 0 when the track pins under the navbar, 1 when it unpins.
    offset: [`start ${STICKY_TOP_PX}px`, "end end"],
  });

  useLayoutEffect(() => {
    const measure = () => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      const visible = viewport.clientHeight;
      setTravels((prev) => {
        const next = colRefs.current.map((el) =>
          el ? Math.max(0, el.offsetHeight - visible) : 0
        );
        return next.some((v, i) => v !== prev[i]) ? next : prev;
      });
    };
    measure();

    const ro = new ResizeObserver(measure);
    if (viewportRef.current) ro.observe(viewportRef.current);
    colRefs.current.forEach((el) => el && ro.observe(el));
    return () => ro.disconnect();
  }, [columns]);

  // Every column sweeps its full height so all images pass through view.
  // Outer columns ride upward against the page scroll; the middle one runs
  // in reverse (bottom-aligned → top-aligned), drifting downward.
  const y0Raw = useTransform(scrollYProgress, [0, 1], [0, -travels[0]]);
  const y1Raw = useTransform(scrollYProgress, [0, 1], [-travels[1], 0]);
  const y2Raw = useTransform(scrollYProgress, [0, 1], [0, -travels[2]]);

  const spring = { stiffness: 70, damping: 22, mass: 0.6 };
  const y0 = useSpring(y0Raw, spring);
  const y1 = useSpring(y1Raw, spring);
  const y2 = useSpring(y2Raw, spring);
  const yValues = [y0, y1, y2];

  const maxTravel = Math.max(0, ...travels);

  return (
    <div
      ref={trackRef}
      className={styles.desktopTrack}
      style={{
        height: `calc(100vh - ${STICKY_TOP_PX}px + ${Math.round(maxTravel * DESKTOP_SCROLL_FACTOR)}px)`,
      }}
    >
      <div className={styles.desktopSticky}>
        <div className={styles.desktopHeader}>
          {titleNode}
        </div>
        <div ref={viewportRef} className={styles.desktopColumns}>
          {columns.map((col, i) => (
            <motion.div
              key={i}
              ref={(el) => { colRefs.current[i] = el; }}
              className={styles.parallaxCol}
              style={{ y: yValues[i] }}
            >
              {col.map((item) => (
                <figure key={item.id} className={styles.parallaxFigure}>
                  <div className={styles.parallaxImageWrapper}>
                    {item.cloudinaryPublicId ? (
                      <CldImage
                        publicId={item.cloudinaryPublicId}
                        alt={item.event?.title || "Highlight Image"}
                        width={800} height={700}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Image
                        src={item.imageUrl}
                        alt={item.event?.title || "Highlight Image"}
                        width={800} height={700}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    )}
                  </div>
                  <figcaption className={styles.parallaxCaption}>
                    {(item.event_id ? eventTitleById.get(item.event_id) : undefined) ?? item.event?.title}
                  </figcaption>
                </figure>
              ))}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Mobile

function MobileCard({ item, eventTitleById }: { item: ShowcaseImage, eventTitleById: Map<string, string> }) {
  return (
    <div className={styles.mobileCard}>
      <div className={styles.mobileImageWrapper}>
        {item.cloudinaryPublicId ? (
          <CldImage
            publicId={item.cloudinaryPublicId}
            alt={item.event?.title || ""}
            width={400} height={300}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <Image
            src={item.imageUrl}
            alt={item.event?.title || ""}
            width={400} height={300}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        )}
      </div>
      <p className={styles.mobileCaption}>{(item.event_id ? eventTitleById.get(item.event_id) : undefined) ?? item.event?.title}</p>
    </div>
  );
}

function MobileHorizontalScroll({ items, titleNode, eventTitleById }: { items: ShowcaseImage[], titleNode: React.ReactNode, eventTitleById: Map<string, string> }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const maskRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const locale = useLocale();
  const isRTL = locale === "ar";

  // How far each row must translate so its last card ends fully visible.
  const [travels, setTravels] = useState<number[]>([0, 0]);

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: [`start ${STICKY_TOP_PX}px`, "end end"],
  });

  useLayoutEffect(() => {
    const measure = () => {
      const mask = maskRef.current;
      if (!mask) return;
      const visible = mask.clientWidth;
      setTravels((prev) => {
        const next = rowRefs.current.map((el) =>
          el ? Math.max(0, el.scrollWidth - visible) : 0
        );
        return next.some((v, i) => v !== prev[i]) ? next : prev;
      });
    };
    measure();

    const ro = new ResizeObserver(measure);
    if (maskRef.current) ro.observe(maskRef.current);
    rowRefs.current.forEach((el) => el && ro.observe(el));
    return () => ro.disconnect();
  }, [items]);

  // Rows move in opposite directions, each traversing exactly its own
  // overflow so every card passes fully through view. Mirrored for RTL,
  // where the overflow extends to the left instead of the right.
  const dir = isRTL ? 1 : -1;
  const x1Raw = useTransform(scrollYProgress, [0, 1], [dir * travels[0], 0]);
  const x2Raw = useTransform(scrollYProgress, [0, 1], [0, dir * travels[1]]);

  const spring = { stiffness: 70, damping: 20, mass: 0.8 };
  const x1 = useSpring(x1Raw, spring);
  const x2 = useSpring(x2Raw, spring);

  const half = Math.ceil(items.length / 2);
  const row1Items = items.slice(0, half);
  const row2Items = items.slice(half);

  const maxTravel = Math.max(0, ...travels);

  return (
    <div
      ref={trackRef}
      className={styles.mobileTrack}
      style={{
        height: `calc(100dvh - ${STICKY_TOP_PX}px + ${Math.round(maxTravel * MOBILE_SCROLL_FACTOR)}px)`,
      }}
    >
      <div className={styles.mobileSticky}>
        <div className={styles.mobileHeader}>
          {titleNode}
        </div>
        <div className={styles.mobileRows}>
          <div className={styles.mobileRowMask} ref={maskRef}>
            <motion.div
              ref={(el) => { rowRefs.current[0] = el; }}
              className={styles.mobileRow}
              style={{ x: x1 }}
            >
              {row1Items.map((item) => (
                <MobileCard key={item.id} item={item} eventTitleById={eventTitleById} />
              ))}
            </motion.div>
          </div>

          <div className={styles.mobileRowMask}>
            <motion.div
              ref={(el) => { rowRefs.current[1] = el; }}
              className={styles.mobileRow}
              style={{ x: x2 }}
            >
              {row2Items.map((item) => (
                <MobileCard key={item.id} item={item} eventTitleById={eventTitleById} />
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main component

export default function GalleryTab({
  events,
  highlights
}: {
  events: FinishedEvent[],
  highlights: ShowcaseImage[]
}) {
  const t = useTranslations("MediaPage");
  const [activeFilter, setActiveFilter] = useState(t("recent"));

  const ITEMS_PER_PAGE = 6;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    setIsDesktop(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const eventTitleById = new Map(events.map(e => [e.id, e.title]));

  const columns = splitIntoColumns(highlights, 3);

  const titleNode = (
    <h2 className={styles.highlightsTitle}>{t("highlights")}</h2>
  );

  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [activeFilter]);

  const years = [...new Set(events.map(e => new Date(e.date).getFullYear()))].sort((a, b) => b - a);
  const filters = [t("recent"), ...years.map(String)];

  const filteredEvents = events.filter((event) => {
    if (activeFilter === t("recent")) return true;
    return event.date ? event.date.includes(activeFilter) : true;
  });

  const displayedEvents = filteredEvents.slice(0, visibleCount);

  const hasMore = visibleCount < filteredEvents.length;

  return (
    <section className={`${styles.galleryTab} ${styles.section}`}>

      <div className={styles.galleryEventsSection}>
        <div className={styles.filters}>
          {filters.map((filter) => (
            <button
              key={filter}
              className={activeFilter === filter ? styles.filterBtnActive : styles.filterBtn}
              onClick={() => setActiveFilter(filter)}
            >
              {filter}
            </button>
          ))}
        </div>

        <div className={styles.grid}>
          {displayedEvents.map((event) => (
            <Link href={event.driveLink || "#"} key={event.id} target="_blank" rel="noopener noreferrer">
              <div className={styles.card}>
                <div className={styles.imageWrapper}>
                  {event.coverImagePublicId ? (
                    <CldImage
                      publicId={event.coverImagePublicId}
                      alt={event.title}
                      className={styles.galleryImage}
                      width={800} height={600}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <Image
                      src={event.coverImageLink}
                      alt={event.title}
                      className={styles.galleryImage}
                      width={800} height={600}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  )}
                  <div className={styles.cardOverlay}>
                    <span className={styles.overlayButton}>
                      {t("viewGallery")}
                    </span>
                  </div>
                </div>
                <p className={styles.caption}>{event.title}</p>
              </div>
            </Link>
          ))}
        </div>

        {hasMore && (
          <button
            className={styles.viewMoreBtn}
            onClick={() => setVisibleCount((prev) => prev + ITEMS_PER_PAGE)}
          >
            {t("viewMore")}
          </button>
        )}
      </div>

      {/* Highlights*/}
      {isDesktop === null ? (
        <div>{titleNode}</div>
      ) : isDesktop ? (
        <DesktopParallax columns={columns} titleNode={titleNode} eventTitleById={eventTitleById} />
      ) : (
        <MobileHorizontalScroll items={highlights} titleNode={titleNode} eventTitleById={eventTitleById} />
      )}

    </section>
  );
}
